import { SANITY } from '~/services/sanity.service'
import { AppLanguage } from '~/types/locale'
import { Topic, TopicData, TopicOverview, FileTopic, PreviewTopic, TopicGroq, SimpleTopic, Path } from '~/types/theory'

export function isFile(topic: Topic | SimpleTopic): topic is FileTopic {
  return ('previewUrl' in topic && !!topic?.previewUrl) || ('smallPreviewUrl' in topic && !!topic?.smallPreviewUrl)
}

function addLanguageToPath(path: string, language: AppLanguage): string {
  return language === 'en' ? path : `/${language}${path}`
}

function getDestinationPath(path: string, destination: Path): string {
  const isSlug = destination.slug
  const destinationPath = addLanguageToPath(destination.path, destination.language)
  if (!isSlug) return destinationPath
  const destinationParts = destination.path.split('/')
  const redirectPath = path
    .split('/')
    .map((part, index) => (index >= destinationParts.length ? part : destinationParts[index]))
    .join('/')
  return addLanguageToPath(redirectPath, destination.language)
}

function getShouldRenderChildren(topic: TopicOverview): boolean {
  if (!topic.children) return false
  return !topic.children.some((child) => !child.children)
}

export function populateShouldRenderChildren(
  topicOverview: TopicOverview[],
  breadcrumbs?: TopicData[],
): TopicOverview[] {
  return topicOverview.map((overview) => {
    const shouldRenderChildren = getShouldRenderChildren(overview)
    const newOverview: TopicOverview = {
      h1: overview.h1,
      path: overview.path,
      ...(shouldRenderChildren ? { shouldRenderChildren: true } : undefined),
    }

    if (overview.children && (!breadcrumbs || (breadcrumbs[1] && overview.path === breadcrumbs[1].path))) {
      const newBreadcrumbs = breadcrumbs?.slice(1)
      return { ...newOverview, children: populateShouldRenderChildren(overview.children, newBreadcrumbs) }
    }
    return newOverview
  })
}

function getWithPreviewTopics(topic: TopicGroq): {
  topic: Topic
  previewTopics: PreviewTopic[]
} {
  const shouldRenderChildren = getShouldRenderChildren(topic)
  const newTopic = shouldRenderChildren === true ? { ...topic, shouldRenderChildren } : topic
  const { children, ...rest } = newTopic

  if (!children || ('isRootPath' in newTopic && newTopic.isRootPath)) return { topic: rest, previewTopics: [] }

  return {
    topic: rest,
    previewTopics: children.flatMap((child) => {
      const { previewTopics } = getWithPreviewTopics(child)
      return isFile(child)
        ? [{ path: child.path, h1: child.h1, smallPreviewUrl: child.smallPreviewUrl }]
        : previewTopics
    }),
  }
}

export const getTopicData = async ({
  paths,
  language,
}: {
  paths: string[]
  language: AppLanguage
}): Promise<
  | { topic: Topic; topicOverview: TopicOverview[]; previewTopics: TopicData[]; encyclopediaTopic: TopicGroq }
  | { destinationPath: string }
  | undefined
> => {
  try {
    const path = `/${paths.join('/')}`
    const encyclopediaTopic = await SANITY.getEncyclopediaTopic({ language, path })
    if (!encyclopediaTopic.key) {
      const redirect = await SANITY.getEncyclopediaRedirect({ language, path })
      if (!redirect?.destination) {
        const alternativeLanguagePath = await SANITY.getAlternativeLanguagePath({ language, path })
        if (!alternativeLanguagePath?.path) throw new Error(`No article or redirect for ${path}`)
        // Prefer the requested-language translation (sharedLanguageKey sibling) so
        // /no/<english-slug> lands on the NO topic instead of the EN page. With no key
        // there is nothing to identify the translation with, so fall back to the document
        // that owns the path and send the visitor to its own locale. The path guard keeps
        // a self-referencing sibling from redirecting to the URL we are already on.
        const { translated } = alternativeLanguagePath
        const destination = translated?.path && translated.path !== path ? translated : alternativeLanguagePath
        const destinationPath = getDestinationPath(path, destination)
        return { destinationPath }
      }
      const destinationPath = getDestinationPath(path, redirect.destination)
      return { destinationPath }
    }
    const topOverview = await SANITY.getEncyclopediaTopOverview({ language, type: encyclopediaTopic.type })
    if (!topOverview || !topOverview.topicOverview) {
      throw Error(`Missing Topic Overview for ${path}`)
    }

    const filteredTopicOverview = populateShouldRenderChildren(topOverview.topicOverview, encyclopediaTopic.breadcrumbs)
    const { topic, previewTopics } = getWithPreviewTopics(encyclopediaTopic)
    return { topic, previewTopics, topicOverview: filteredTopicOverview, encyclopediaTopic }
  } catch (error) {
    console.error(error)
    return
  }
}
